import {
    get,
    getUrlSearchParams,
    isEmpty,
    isNil,
    SuiteId,
    URL_PARAMETER,
    ValueOf
} from '@libs/shared';
import Intl, { LOCALE_KEY } from '@fx-ui/intl';
import siteRouterManager from '~/utils/site-router';
import { getCurrentLocaleKey } from './site';
import TextLogo from '~/assets/logo-text.png';
import PureLogo from '~/assets/logo.png';
import PlatformLogoWithText from '~/assets/suite-images/logo/platform_logo_with_text.png';
import PlatformLogo from '~/assets/suite-images/logo/platform_logo.png';
import PlatformBanner from '~/assets/suite-images/banner/platform_banner.png';
import PlatformCustomLoginBanner from '~/assets/suite-images/banner/platform_custom_login_banner.png';
import { getCustomLoginBanner, getLoginBanner } from '~envs/utils';

/**
 * 获取产品名称
 * fx-code 返回 jdy
 * fx-corp 返回 fr
 *
 * @deprecated 未迁移功能拆分后会删除，平台侧功能优先使用 getBrandInfo().brandName 代替
 */
export const getProductName = () => {
    const currentLocale = getCurrentLocaleKey() as keyof typeof window.jdy_config.brand_name;
    return get(window.jdy_config, `brand_name.${currentLocale}`, '');
};

/**
 * 获取产品logo
 * fx-code 返回 jdy logo
 * fx-corp 返回 fr logo
 *
 * @deprecated 未迁移功能拆分后会删除，平台侧功能优先使用 getBrandInfo().logoWithText、getBrandInfo().logo 代替
 * @param withText
 */
export const getProductLogo = (withText = true) => {
    if (withText) {
        return get(window.jdy_config, `brand_logo.text`, TextLogo);
    }
    return get(window.jdy_config, ['brand_logo', 'pure'], PureLogo);
};

/**
 * 获取 Suite 名称，兼容 `fx-code` 渲染平台 Pug 模板的场景
 * fx-code、fx-corp 需返回相同配置
 *
 * @deprecated 未迁移功能拆分后会删除，平台侧功能优先使用 getSuiteView 代替
 * @param suiteId SuiteId
 */
export const getSuiteName = (suiteId: number) => {
    const currentLocale = getCurrentLocaleKey();
    const defaultName = get(
        window.jdy_config,
        ['suite_name', suiteId, LOCALE_KEY.ZH_CN, 'name'],
        ''
    );
    return get(window.jdy_config, ['suite_name', suiteId, currentLocale, 'name'], defaultName);
};

export type SuiteView = Omit<ValueOf<NonNullable<typeof window.jdy_config.suite_views>>, 'name'> & {
    name: string;
};

/**
 * 获取平台和子产品品牌要素
 * `suite_views` 仅由 `fx-corp` 注入，`fx-code` 无该配置
 */
export const getResolvedSuiteViews = () => {
    const suite_views = window.jdy_config.suite_views || {};

    // suite_views 为空时，代表 fx-code 渲染场景，此时平台元素展示 jdy
    if (isEmpty(suite_views)) {
        const jdyView = getJdyView();
        return {
            [String(SuiteId.PLATFORM)]: jdyView,
            [String(SuiteId.APP_BUILDER)]: jdyView
        };
    }

    const currentLocaleKey = getCurrentLocaleKey();
    const suiteViews: Record<string, SuiteView> = {};

    Object.entries(window.jdy_config.suite_views || {}).forEach(([suiteId, view]) => {
        let suiteView = {
            ...view,
            name: view.name?.[currentLocaleKey] || ''
        };
        switch (suiteId) {
            case String(SuiteId.PLATFORM): {
                suiteView = {
                    ...suiteView,
                    icon_with_text_uri: suiteView.icon_with_text_uri || PlatformLogoWithText,
                    icon: suiteView.icon || PlatformLogo,
                    banner_uri: suiteView.banner_uri || PlatformBanner,
                    custom_login_banner_uri:
                        suiteView.custom_login_banner_uri || PlatformCustomLoginBanner
                };
                break;
            }
        }
        suiteViews[suiteId] = suiteView;
    });

    return suiteViews;
};

/**
 * 获取平台品牌信息
 *
 * @param customBrandInfo
 */
export const getBrandInfo = (customBrandInfo?: { loginBanner?: string; logoWithText?: string }) => {
    const suiteViews = getResolvedSuiteViews();

    const platformSuiteView = suiteViews?.[SuiteId.PLATFORM];
    const platformLogoWithText = platformSuiteView?.icon_with_text_uri;
    const platformLogo = platformSuiteView?.icon;
    const platformLoginBanner = platformSuiteView?.banner_uri;
    const platformCustomLoginBanner = platformSuiteView?.custom_login_banner_uri;
    const platformName = platformSuiteView?.name;

    return {
        // 平台logo
        logoWithText: customBrandInfo?.logoWithText || platformLogoWithText,
        logo: platformLogo,
        // 平台登录页 banner
        loginBanner: customBrandInfo?.loginBanner || platformLoginBanner,
        // 平台自定义登录页 banner
        customLoginBanner: platformCustomLoginBanner,
        // 平台品牌名
        brandName: platformName,
        loginTypes: window.jdy_login_type_config,
        loginTitle: Intl.get('account.signin.account'),
        registerTitle: Intl.get('account.register.jdy'),
        termServiceLink: siteRouterManager.getHomePage('term_index'),
        termPrivacyLink: siteRouterManager.getHomePage('term_privacy'),
        showAnalyticsSetting: true,
        hasRegisterAnimation: true,
        // 当前环境可用套件信息（含平台）
        suiteViews
    };
};

/**
 * 获取指定 suite 的品牌信息
 * `suite_views` 仅由 `fx-corp` 注入，`fx-code` 无该配置
 *
 * @param suiteId
 */
export const getSuiteView = (suiteId: SuiteId) => {
    return getResolvedSuiteViews()[suiteId];
};

/**
 * 兼容通过 fx-code 渲染 pug 场景
 *
 * @deprecated 未迁移功能拆分后会删除
 */
export const getJdyView = (): Partial<SuiteView> => {
    return {
        name: getProductName(),
        icon: getProductLogo(false),
        icon_with_text_uri: getProductLogo(true),
        home_uri: siteRouterManager.getHomePage(),
        banner_uri: getLoginBanner(),
        custom_login_banner_uri: getCustomLoginBanner()
    };
};

/**
 * 从 URL 中取 suiteId，queryKey = suite_id
 * 不存在时返回 SuiteId.PLATFORM
 */
export const getSuiteIdByUrl = () => {
    const suiteId = getUrlSearchParams(URL_PARAMETER.SUITE_ID);
    return isNil(suiteId) ? SuiteId.PLATFORM : Number(suiteId);
};

/**
 * 获取当前 suiteId，不存在时返回 SuiteId.PLATFORM
 */
export const getCurrentSuiteId = () => {
    return window.platform_suite_id ?? getSuiteIdByUrl();
};
